import { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Check } from 'lucide-react';

interface TutorialTooltipProps {
  step: {
    id: string;
    type: 'passive' | 'interactive';
    title: string;
    description: string;
    position: 'top' | 'bottom' | 'left' | 'right' | 'center';
    interactionHint?: string;
  };
  rect: { top: number; left: number; width: number; height: number } | null;
  currentStepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  showSuccess?: boolean;
}

const TOOLTIP_GAP = 16;
const EDGE_PADDING = 16;
const ARROW_SIZE = 8;

function computePosition(
  position: TutorialTooltipProps['step']['position'],
  rect: TutorialTooltipProps['rect']
) {
  if (!rect || position === 'center') {
    return {
      style: {
        position: 'fixed' as const,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      },
      arrowSide: null,
    };
  }

  const style: React.CSSProperties = { position: 'fixed' };

  // We compute approximate positioning, then clamp with CSS clamp
  // The tooltip width is max 360px
  const tooltipWidth = 360;
  const tooltipHeight = 220; // estimate

  let top = 0;
  let left = 0;
  let arrowSide: 'top' | 'bottom' | 'left' | 'right' | null = null;

  switch (position) {
    case 'bottom': {
      top = rect.top + rect.height + TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
      arrowSide = 'top';
      break;
    }
    case 'top': {
      top = rect.top - tooltipHeight - TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
      arrowSide = 'bottom';
      break;
    }
    case 'left': {
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.left - tooltipWidth - TOOLTIP_GAP;
      arrowSide = 'right';
      break;
    }
    case 'right': {
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.left + rect.width + TOOLTIP_GAP;
      arrowSide = 'left';
      break;
    }
  }

  // Clamp to viewport
  const maxLeft = window.innerWidth - tooltipWidth - EDGE_PADDING;
  const maxTop = window.innerHeight - tooltipHeight - EDGE_PADDING;
  left = Math.max(EDGE_PADDING, Math.min(left, maxLeft));
  top = Math.max(EDGE_PADDING, Math.min(top, maxTop));

  style.top = `${top}px`;
  style.left = `${left}px`;

  return { style, arrowSide };
}

function Arrow({ side }: { side: 'top' | 'bottom' | 'left' | 'right' }) {
  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    width: 0,
    height: 0,
  };

  switch (side) {
    case 'top':
      return (
        <div
          style={{
            ...baseStyle,
            top: -ARROW_SIZE,
            left: '50%',
            transform: 'translateX(-50%)',
            borderLeft: `${ARROW_SIZE}px solid transparent`,
            borderRight: `${ARROW_SIZE}px solid transparent`,
            borderBottom: `${ARROW_SIZE}px solid var(--color-bg-overlay)`,
          }}
        />
      );
    case 'bottom':
      return (
        <div
          style={{
            ...baseStyle,
            bottom: -ARROW_SIZE,
            left: '50%',
            transform: 'translateX(-50%)',
            borderLeft: `${ARROW_SIZE}px solid transparent`,
            borderRight: `${ARROW_SIZE}px solid transparent`,
            borderTop: `${ARROW_SIZE}px solid var(--color-bg-overlay)`,
          }}
        />
      );
    case 'left':
      return (
        <div
          style={{
            ...baseStyle,
            left: -ARROW_SIZE,
            top: '50%',
            transform: 'translateY(-50%)',
            borderTop: `${ARROW_SIZE}px solid transparent`,
            borderBottom: `${ARROW_SIZE}px solid transparent`,
            borderRight: `${ARROW_SIZE}px solid var(--color-bg-overlay)`,
          }}
        />
      );
    case 'right':
      return (
        <div
          style={{
            ...baseStyle,
            right: -ARROW_SIZE,
            top: '50%',
            transform: 'translateY(-50%)',
            borderTop: `${ARROW_SIZE}px solid transparent`,
            borderBottom: `${ARROW_SIZE}px solid transparent`,
            borderLeft: `${ARROW_SIZE}px solid var(--color-bg-overlay)`,
          }}
        />
      );
  }
}

export function TutorialTooltip({
  step,
  rect,
  currentStepIndex,
  totalSteps,
  onNext,
  onBack,
  onSkip,
  showSuccess = false,
}: TutorialTooltipProps) {
  const { style, arrowSide } = useMemo(
    () => computePosition(step.position, rect),
    [step.position, rect]
  );

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={step.id}
        initial={{ opacity: 0, scale: 0.95, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 8 }}
        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="surface-overlay z-[2001] flex flex-col gap-3 p-4"
        style={{
          ...style,
          width: 'min(360px, calc(100vw - 2rem))',
        }}
      >
        {/* Arrow */}
        {arrowSide && <Arrow side={arrowSide} />}

        {/* Title */}
        <h3 className="text-title text-text-primary">{step.title}</h3>

        {/* Description */}
        <p className="text-caption text-text-secondary">{step.description}</p>

        {/* Action area */}
        <div className="flex items-center justify-between min-h-[36px]">
          {showSuccess ? (
            <div className="flex items-center gap-2 text-success">
              <Check size={16} strokeWidth={2.5} />
              <span className="text-caption font-semibold">Nice!</span>
            </div>
          ) : step.type === 'passive' ? (
            <>
              <div>
                {currentStepIndex > 0 && (
                  <button
                    onClick={onBack}
                    className="flex items-center gap-1 px-2 py-1.5 text-caption text-text-muted hover:text-text-secondary transition-colors rounded-lg"
                  >
                    <ChevronLeft size={14} />
                    Back
                  </button>
                )}
              </div>
              <button
                onClick={onNext}
                className="flex items-center gap-1 px-3 py-1.5 bg-accent text-white text-caption font-semibold rounded-lg hover:bg-accent-hover transition-colors"
              >
                Next
                <ChevronRight size={14} />
              </button>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-accent" />
              </span>
              <span className="text-caption text-text-secondary">
                {step.interactionHint}
              </span>
            </div>
          )}
        </div>

        {/* Step dots */}
        <div className="flex items-center justify-center gap-1.5 pt-1">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`h-2 rounded-full transition-all duration-200 ${
                i === currentStepIndex
                  ? 'w-5 bg-accent'
                  : 'w-2 bg-text-muted/30'
              }`}
            />
          ))}
        </div>

        {/* Skip link */}
        <button
          onClick={onSkip}
          className="text-micro text-text-muted hover:underline self-center pt-0.5"
        >
          Skip tutorial
        </button>
      </motion.div>
    </AnimatePresence>
  );
}
